import { isUndefined } from "./TypeUtil";

/**
 * CacheUtil.js
 * 
 * Created on 11-dec-2014, 13:50:24
 * Changelog:
 * 2017-10-19 Reinier Feijen: added support for margins and scale, added documentation to some methods
 * 
 * @author Harm Boschloo for Ranj
 **/
const CacheUtil = {};
export default CacheUtil;

CacheUtil.cachedRectangleName = "__cached_rectangle__";

/**
 * Cache displayObject, optionally using specified bounds
 * If only x and y are specified, x and y are interpreted as margins! 
 * Specifying margins can be helpful for displayObjects with blur filters etc.
 * @param {DisplayObject} displayObject
 * @param {Number} x Optional. If width and height are not specified, this argument is used as margin
 * @param {Number} y Optional. If width and height are not specified, this argument is used as margin
 * @param {Number} width Optional. 
 * @param {Number} height Optional. 
 * @param {Number} scale Optional scale for caching. Useful for bitmap content that are not shown at native size. 
 * @returns {undefined}
 */
CacheUtil.cache = function(displayObject, x, y, width, height, scale) {
    var bounds = CacheUtil.getBounds(displayObject, x, y, width, height);
    if (bounds) {
        scale = scale || 1
        displayObject.cache(bounds.x, bounds.y, bounds.width, bounds.height, scale);
    }
};

CacheUtil.uncache = function(displayObject) {
    displayObject.uncache();
};

CacheUtil.updateCache = function(displayObject, includeChildren) {
    if (displayObject.cacheCanvas) {
        displayObject.updateCache();
    }
    if (includeChildren && displayObject.children) {
        for (var i = 0; i < displayObject.children.length; ++i) {
            CacheUtil.updateCache(displayObject.children[i]);
        }
    }
};


CacheUtil.recache = function(displayObject, x, y, width, height, scale) {
    CacheUtil.uncache(displayObject);
    CacheUtil.cache(displayObject, x, y, width, height, scale);
};

CacheUtil.cacheStaticChildren = function(displayObject, dynamicChildren, x, y, width, height, scale) {
    if (!displayObject.children) {
        return;
    }

    var bounds = CacheUtil.getBounds(displayObject, x, y, width, height);

    var staticContainer = new createjs.Container();
    if (ranj.DEBUG) {
        staticContainer.name = "static";
    }

    for (var i = 0; i < displayObject.children.length;) {
        var child = displayObject.children[i];
        if (dynamicChildren.contains(child)) {
            ++i;
        } else {
            displayObject.removeChildAt(i);
            staticContainer.addChild(child);
        }
    }

    staticContainer.cache(bounds.x, bounds.y, bounds.width, bounds.height, scale);
    displayObject.addChildAt(staticContainer, 0);

    // don't disable mouse for swiping
    staticContainer.mouseEnabled = false;
};

CacheUtil.showCachedRectangle = function(displayObject) {
    if (displayObject.cacheCanvas) {
        CacheUtil.showBoundsRectangle(displayObject, displayObject._cacheOffsetX, displayObject._cacheOffsetY,
            displayObject._cacheWidth, displayObject._cacheHeight, CacheUtil.cachedRectangleName);
    }
};

CacheUtil.showCachedRectangles = function(displayObject) {
    if (displayObject.cacheCanvas) {
        CacheUtil.showCachedRectangle(displayObject);
    }

    if (displayObject.children) {
        for (var i = 0; i < displayObject.children.length; ++i) {
            CacheUtil.showCachedRectangles(displayObject.children[i]);
        }
    }
};

CacheUtil.clearCachedRectangles = function(displayObject) {
    if (displayObject instanceof createjs.Shape && displayObject.name === CacheUtil.cachedRectangleName
        && displayObject.parent) {
        displayObject.parent.removeChild(displayObject);
    } else if (displayObject.children) {
        for (var i = 0; i < displayObject.children.length; ++i) {
            CacheUtil.clearCachedRectangles(displayObject.children[i]);
        }
    }
};

CacheUtil.bindShowCachedRectangles = function(charCode, displayObject) {
    document.addEventListener('keypress', function(event) {
        if (event.charCode === charCode) {
            event.preventDefault();
            CacheUtil.showCachedRectangles(displayObject);
        }
    });
};

CacheUtil.bindClearCachedRectangles = function(charCode, displayObject) {
    document.addEventListener('keypress', function(event) {
        if (event.charCode === charCode) {
            event.preventDefault();
            CacheUtil.clearCachedRectangles(displayObject);
        }
    });
};

CacheUtil.showBoundsRectangle = function(displayObject, x, y, width, height, name) {
    var bounds = CacheUtil.getBounds(displayObject, x, y, width, height);

    var shape = new createjs.Shape();
    shape.graphics.setStrokeStyle(4);
    shape.graphics.beginStroke("#f00");
    shape.graphics.drawRect(bounds.x, bounds.y, bounds.width, bounds.height);
    shape.graphics.endStroke();
    shape.alpha = 0.3;
    shape.x = displayObject.x - displayObject.regX;
    shape.y = displayObject.y - displayObject.regY;
    shape.name = name;

    var parent = displayObject.parent || displayObject.stage;
    parent.addChild(shape);
};

/**
 * Obtain bounds of displayObject, optionally using specified bounds or margins
 * If only x and y are specified, x and y are interpreted as margins!
 * @param {DisplayObject} displayObject
 * @param {Number} x Optional. If width and height are not specified, this argument is used as horizontal margin
 * @param {Number} y Optional. If width and height are not specified, this argument is used as vertical margin
 * @param {Number} width Optional. 
 * @param {Number} height Optional. 
 * @returns {createjs.Rectangle}
 */
CacheUtil.getBounds = function(displayObject, x, y, width, height) {
    if (!isUndefined(x) && !isUndefined(y) && !isUndefined(width) && !isUndefined(height)) {
        return new createjs.Rectangle(x, y, width, height);
    }

    var bounds = displayObject.getBounds() || displayObject.nominalBounds;
    if (bounds) {
        //Apply margins
        var mh = x ? x : 0;
        var mv = y ? y : 0;
        bounds.pad(mv, mh, mv, mh);
    }
    else {
        console.warn("CacheUtil.getBounds, no bounds for " + displayObject);
    }
    return bounds;
};
